import { useState, useEffect, useMemo } from 'react';
import api from '../api';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { BellIcon, AlertIcon, ClockIcon, ShieldIcon, SearchIcon, ChevronLeftIcon, ChevronRightIcon, MailIcon, DownloadIcon } from '../components/Icons';

function initials(name = '') { return name.split(' ').slice(0, 2).map(n => n[0] || '').join('').toUpperCase(); }
function avatarGradient(name = '') {
  const g = ['linear-gradient(135deg,#667eea,#764ba2)', 'linear-gradient(135deg,#fb866d,#e8614a)', 'linear-gradient(135deg,#1a2744,#2d3f6b)', 'linear-gradient(135deg,#22c55e,#16a34a)', 'linear-gradient(135deg,#f59e0b,#d97706)', 'linear-gradient(135deg,#8b5cf6,#7c3aed)'];
  let h = 0; for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return g[Math.abs(h) % g.length];
}

const lvl = {
  expired: { dot: 'bg-red-500', badge: 'bg-red-50 text-red-600 border-red-200', text: 'text-red-500', iconBg: 'bg-red-50', icon: AlertIcon },
  critical: { dot: 'bg-[#fb866d]', badge: 'bg-orange-50 text-[#e8614a] border-orange-200', text: 'text-[#e8614a]', iconBg: 'bg-[#fde8e3]', icon: ClockIcon },
  warning: { dot: 'bg-amber-400', badge: 'bg-amber-50 text-amber-700 border-amber-200', text: 'text-amber-500', iconBg: 'bg-amber-50', icon: BellIcon },
};

const TABS = [
  { key: 'iqama', label: 'Iqama' },
  { key: 'qiwa', label: 'Qiwa' },
  { key: 'passport', label: 'Passport' },
  { key: 'insurance', label: 'Insurance' },
  { key: 'leave', label: 'Annual Leave' },
  { key: 'contracts', label: 'Contracts' },
];

const LIMIT = 12;

export default function Compliance() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('iqama');
  const [level, setLevel] = useState('all');
  const [search, setSearch] = useState('');
  const [filterProject, setFP] = useState('All');
  const [filterSponsor, setFSp] = useState('All');
  const [projects, setProjects] = useState([]);
  const [sponsorships, setSponsorships] = useState([]);
  const [page, setPage] = useState(1);
  const [digestState, setDigestState] = useState('idle'); // idle | sending | sent | disabled | error

  const TYPE_LABEL = { iqama: 'Iqama Expiry', qiwa: 'Qiwa Expiry', passport: 'Passport Expiry', insurance: 'Health Insurance Expiry', leave: 'Annual Leave Due', contracts: 'Client Contract Expiry' };

  function matchesFilters(a) {
    if (level !== 'all' && a.level !== level) return false;
    if (filterProject !== 'All' && a.project !== filterProject) return false;
    if (filterSponsor !== 'All' && a.sponsorship !== filterSponsor) return false;
    const q = search.toLowerCase();
    if (q && !(
      a.name.toLowerCase().includes(q) ||
      (a.iqamaNo || '').includes(q) ||
      (a.passportNo || '').toLowerCase().includes(q) ||
      (a.project || '').toLowerCase().includes(q)
    )) return false;
    return true;
  }

  function rowShape(a) {
    return {
      Type: TYPE_LABEL[a.alertType] || a.alertType, Name: a.name, Project: a.project || '',
      Nationality: a.nationality || '', Sponsorship: a.sponsorship || '',
      'Iqama No': a.iqamaNo || '', 'Passport No': a.passportNo || '',
      Date: a.expiryDate, Status: a.days < 0 ? `Expired ${Math.abs(a.days)}d ago` : `${a.days}d left`,
      Level: a.level,
    };
  }

  function exportRows(scope) { // 'tab' | 'all' — always respects the active filters
    return (scope === 'all' ? alerts : alerts.filter(a => a.alertType === tab))
      .filter(matchesFilters).map(rowShape);
  }

  function fileSuffix() {
    const parts = [];
    if (filterProject !== 'All') parts.push(filterProject.replace(/\s+/g, '_'));
    if (filterSponsor !== 'All') parts.push(filterSponsor.replace(/\s+/g, '_'));
    if (level !== 'all') parts.push(level);
    return parts.length ? '-' + parts.join('-') : '';
  }

  function exportExcel(scope) {
    const wb = XLSX.utils.book_new();
    if (scope === 'all') {
      // one sheet per compliance type
      Object.keys(TYPE_LABEL).forEach(t => {
        const rows = alerts.filter(a => a.alertType === t).filter(matchesFilters).map(rowShape);
        if (rows.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), TYPE_LABEL[t].replace(' Expiry', '').replace(' Due', ''));
      });
      if (!wb.SheetNames.length) return;
    } else {
      const rows = exportRows('tab');
      if (!rows.length) return;
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), TYPE_LABEL[tab] || tab);
    }
    XLSX.writeFile(wb, `compliance-${scope === 'all' ? 'all-types' : tab}${fileSuffix()}-${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  function exportPDF(scope) {
    const rows = exportRows(scope);
    if (!rows.length) return;
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const M = 40;
    let y = 48;

    doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.setTextColor(15, 27, 51);
    doc.text(`Delta HR — Compliance Report${scope === 'all' ? '' : ' — ' + (TYPE_LABEL[tab] || tab)}`, M, y);
    y += 16;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(100, 116, 139);
    const filterNote = [
      filterProject !== 'All' ? `Project: ${filterProject}` : null,
      filterSponsor !== 'All' ? `Sponsorship: ${filterSponsor}` : null,
      level !== 'all' ? `Level: ${level}` : null,
      search ? `Search: "${search}"` : null,
    ].filter(Boolean).join(' · ');
    doc.text(`${rows.length} items${filterNote ? ' · ' + filterNote : ''} · generated ${new Date().toLocaleString('en-GB')}`, M, y);
    y += 14;

    const tableFor = (data, startY) => {
      autoTable(doc, {
        startY,
        margin: { left: M, right: M },
        head: [['Employee', 'Project', 'Document No.', 'Date', 'Status']],
        body: data.map(r => [
          `${r.Name}\n${r.Nationality} · ${r.Sponsorship}`,
          r.Project,
          r['Iqama No'] || r['Passport No'] || '—',
          r.Date || '—',
          r.Status,
        ]),
        styles: { fontSize: 8.5, cellPadding: 5, textColor: [30, 41, 59] },
        headStyles: { fillColor: [15, 27, 51], textColor: 255, fontSize: 8, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        didParseCell: (d) => {
          if (d.section === 'body' && d.column.index === 4) {
            d.cell.styles.fontStyle = 'bold';
            d.cell.styles.textColor = String(d.cell.raw).startsWith('Expired') ? [220, 38, 38] : [217, 119, 6];
          }
        },
      });
      return doc.lastAutoTable.finalY;
    };

    if (scope === 'all') {
      Object.keys(TYPE_LABEL).forEach(t => {
        const data = alerts.filter(a => a.alertType === t).filter(matchesFilters).map(rowShape);
        if (!data.length) return;
        if (y > doc.internal.pageSize.getHeight() - 120) { doc.addPage(); y = 48; }
        doc.setFont('helvetica', 'bold'); doc.setFontSize(11.5); doc.setTextColor(15, 27, 51);
        doc.text(`${TYPE_LABEL[t]} (${data.length})`, M, y + 14);
        doc.setDrawColor(251, 134, 109); doc.setLineWidth(1.5);
        doc.line(M, y + 20, doc.internal.pageSize.getWidth() - M, y + 20);
        y = tableFor(data, y + 28) + 10;
      });
    } else {
      tableFor(rows, y + 8);
    }

    doc.save(`compliance-${scope === 'all' ? 'all-types' : tab}${fileSuffix()}-${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  async function sendDigest() {
    setDigestState('sending');
    try {
      const r = await api.post('/compliance/send-digest');
      setDigestState(r.data.sent ? 'sent' : (r.data.disabled ? 'disabled' : 'sent'));
    } catch { setDigestState('error'); }
    setTimeout(() => setDigestState('idle'), 4000);
  }

  useEffect(() => {
    api.get('/employees/alerts', { params: { type: 'all' } })
      .then(r => setAlerts(r.data))
      .finally(() => setLoading(false));
    api.get('/projects').then(r => setProjects(r.data));
    api.get('/sponsorships').then(r => setSponsorships(r.data));
  }, []);

  // reset page + level when tab/filters change
  useEffect(() => { setPage(1); }, [tab, level, search, filterProject, filterSponsor]);

  const tabAlerts = useMemo(() => alerts.filter(a => a.alertType === tab), [alerts, tab]);

  const counts = {
    expired: tabAlerts.filter(a => a.level === 'expired').length,
    critical: tabAlerts.filter(a => a.level === 'critical').length,
    warning: tabAlerts.filter(a => a.level === 'warning').length,
  };

  const filtered = useMemo(() => {
    return tabAlerts.filter(a => {
      if (level !== 'all' && a.level !== level) return false;
      if (filterProject !== 'All' && a.project !== filterProject) return false;
      if (filterSponsor !== 'All' && a.sponsorship !== filterSponsor) return false;
      const q = search.toLowerCase();
      if (q && !(
        a.name.toLowerCase().includes(q) ||
        (a.iqamaNo || '').includes(q) ||
        (a.passportNo || '').toLowerCase().includes(q) ||
        (a.project || '').toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [tabAlerts, level, search, filterProject, filterSponsor]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / LIMIT));
  const paged = filtered.slice((page - 1) * LIMIT, page * LIMIT);
  const pages = [];
  const s = Math.max(1, page - 2), e2 = Math.min(totalPages, s + 4);
  for (let i = s; i <= e2; i++) pages.push(i);

  const hasFilters = search || filterProject !== 'All' || filterSponsor !== 'All' || level !== 'all';
  const selCls = "px-3.5 py-2.5 border border-slate-200 rounded-xl text-[13px] bg-white text-slate-600 outline-none focus:border-[#fb866d] cursor-pointer transition-colors";

  const cards = [
    { key: 'expired', label: 'Expired', sub: 'Immediate action required' },
    { key: 'critical', label: 'Expiring ≤ 30 days', sub: 'Renew soon' },
    { key: 'warning', label: 'Expiring 31–90 days', sub: 'Plan ahead' },
  ];

  const docLabel = { iqama: 'Iqama No.', qiwa: 'Iqama No.', passport: 'Passport No.', insurance: 'Iqama No.', leave: 'Iqama No.', contracts: 'Client' };
  const docValue = (a) => (tab === 'contracts' ? a.name : tab === 'passport' ? a.passportNo : a.iqamaNo) || '—';
  const dateLabel = tab === 'leave' ? 'Leave Due' : tab === 'contracts' ? 'Contract End' : 'Expiry';

  return (
    <div>
      {/* Topbar */}
      <div className="hidden lg:flex bg-white border-b border-slate-100 px-8 py-4 items-center justify-between sticky top-0 z-40">
        <h1 className="text-lg font-bold text-slate-800">Compliance</h1>
        <span className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-xs font-semibold text-slate-600">Admin</span>
      </div>

      <div className="p-4 lg:p-8 stagger">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#0f1b33] flex items-center justify-center">
              <ShieldIcon size={22} className="text-white" />
            </div>
            <div>
              <h2 className="text-[17px] font-bold text-slate-800">Document Expiry Tracking</h2>
              <p className="text-[13px] text-slate-400">Iqama, Qiwa, Passport, Insurance & Annual Leave — daily email digest at 8:00 to HR.</p>
            </div>

          </div>
          {/* Document type tabs */}
          <div className="flex gap-1 p-1 bg-slate-100 rounded-full overflow-x-auto max-w-full self-start">
            {TABS.map(t => (
              <button key={t.key} onClick={() => { setTab(t.key); setLevel('all'); }}
                className={`px-4 lg:px-5 py-2 rounded-full text-[12.5px] lg:text-[13px] font-semibold whitespace-nowrap flex-shrink-0 transition-all ${tab === t.key ? 'bg-[#fb866d] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}>
                {t.label}
                <span className={`ml-2 text-[11px] ${tab === t.key ? 'text-white/80' : 'text-slate-400'}`}>
                  {alerts.filter(a => a.alertType === t.key).length}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Level stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-5 mb-5">
          {cards.map(({ key, label, sub }) => {
            const L = lvl[key];
            const Icon = L.icon;
            return (
              <button key={key} onClick={() => setLevel(level === key ? 'all' : key)}
                className={`bg-white rounded-2xl p-6 text-left transition-all hover:-translate-y-0.5 border-2 ${level === key ? 'border-[#fb866d]' : 'border-slate-100'
                  }`}
                style={{ boxShadow: '0 1px 3px rgba(15,27,51,0.06)' }}>
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${L.iconBg}`}>
                    <Icon size={18} className={L.text} />
                  </div>
                  {level === key && <span className="text-[10px] font-bold text-[#fb866d] uppercase tracking-wide">Filtered</span>}
                </div>
                <p className={`text-3xl font-bold tracking-tight ${L.text}`}>{counts[key]}</p>
                <p className="text-[13.5px] font-semibold text-slate-700 mt-1">{label}</p>
                <p className="text-xs text-slate-400 mt-0.5">{sub}</p>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4 flex items-center gap-3 flex-wrap" style={{ boxShadow: '0 1px 3px rgba(15,27,51,0.06)' }}>
          <div className="relative flex-1 min-w-[220px]">
            <SearchIcon size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-[13.5px] outline-none bg-white text-slate-700 focus:border-[#fb866d] transition-all placeholder:text-slate-400"
              placeholder="Search name, document no., project..."
              value={search} onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select className={selCls} value={filterProject} onChange={e => setFP(e.target.value)}>
            <option value="All">All projects</option>
            {projects.map(p => <option key={p}>{p}</option>)}
          </select>
          <select className={selCls} value={filterSponsor} onChange={e => setFSp(e.target.value)}>
            <option value="All">All sponsorships</option>
            {sponsorships.map(s2 => <option key={s2}>{s2}</option>)}
          </select>
          {hasFilters && (
            <button onClick={() => { setSearch(''); setFP('All'); setFSp('All'); setLevel('all'); }}
              className="px-4 py-2.5 border border-slate-200 rounded-xl text-xs font-semibold text-slate-500 hover:border-red-300 hover:text-red-500 transition-all">
              Clear filters
            </button>
          )}
        </div>

        <div className="hidden sm:flex items-center gap-2 mb-3">
          <button onClick={() => exportPDF('tab')} title="Download this compliance type as PDF"
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-semibold border border-slate-200 text-slate-600 hover:border-[#fb866d] hover:text-[#e8614a] transition-all">
            <DownloadIcon size={13} /> {TABS.find(t => t.key === tab)?.label} PDF
          </button>
          <button onClick={() => exportExcel('tab')} title="Download this compliance type as Excel"
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-semibold border border-slate-200 text-slate-600 hover:border-[#fb866d] hover:text-[#e8614a] transition-all">
            <DownloadIcon size={13} /> {TABS.find(t => t.key === tab)?.label} Excel
          </button>
          <button onClick={() => exportPDF('all')} title="All compliance types — PDF (separate section per type)"
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-semibold border border-slate-200 text-slate-600 hover:border-[#fb866d] hover:text-[#e8614a] transition-all">
            <DownloadIcon size={13} /> All PDF
          </button>
          <button onClick={() => exportExcel('all')} title="All compliance types — Excel (separate sheet per type)"
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-semibold border border-slate-200 text-slate-600 hover:border-[#fb866d] hover:text-[#e8614a] transition-all">
            <DownloadIcon size={13} /> All Excel
          </button>
          <button onClick={sendDigest} disabled={digestState === 'sending'}
          className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full text-[12.5px] font-semibold border border-slate-200 text-slate-600 hover:border-[#fb866d] hover:text-[#e8614a] transition-all disabled:opacity-50">
          <MailIcon size={14} />
          {digestState === 'sending' ? 'Sending...' : digestState === 'sent' ? 'Digest sent ✓' : digestState === 'disabled' ? 'Email disabled in config' : digestState === 'error' ? 'Failed — check SMTP' : 'Send Digest Now'}
        </button>
        
        </div>
        

        {/* Alert table */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(15,27,51,0.06)' }}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <span className="text-[14px] font-bold text-slate-800">
              {TABS.find(t => t.key === tab)?.label} Alerts ({filtered.length})
            </span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 gap-2.5 text-slate-400 text-sm">
              <div className="w-4 h-4 border-2 border-slate-200 border-t-[#fb866d] rounded-full animate-spin" />
              Loading alerts...
            </div>
          ) : paged.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-[15px] font-semibold text-slate-500">No alerts found</div>
              <div className="text-sm text-slate-400 mt-1">
                {tab === 'passport' && tabAlerts.length === 0
                  ? 'No passport expiry dates recorded yet — add them from the employee edit form.'
                  : tab === 'insurance' && tabAlerts.length === 0
                    ? 'No health insurance expiry dates recorded yet — add them from the employee edit form.'
                    : tab === 'leave' && tabAlerts.length === 0
                      ? 'No leave entitlements due within 90 days.'
                      : tab === 'contracts' && tabAlerts.length === 0
                        ? 'No client contracts expiring within 90 days — add contract dates in the Clients page.'
                        : 'All documents in order for this filter.'}
              </div>
            </div>
          ) : paged.map(e => (
            <div key={`${e.id}-${e.alertType}`} className="flex items-center gap-4 px-6 py-3.5 border-b border-slate-50 odd:bg-slate-50/50 last:border-0 hover:bg-[#fde8e3]/40 transition-colors">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${lvl[e.level]?.dot}`} />
              <div className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-[11px] font-bold text-white"
                style={{ background: avatarGradient(e.name) }}>
                {initials(e.name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] font-semibold text-slate-800 truncate">{e.name}</div>
                <div className="text-xs text-slate-400 mt-0.5">{e.project} · {e.nationality || '—'} · {e.sponsorship || 'No sponsorship'}</div>
                {e.contractConflict && (
                  <div className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-50 text-red-600 border border-red-200">
                    ⚠ Qiwa contract ends {new Date(e.qiwaEnd).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} — {e.wasteDays} renewal days fall after it (~SAR {(e.wasteCost || 0).toLocaleString()} wasted unless Qiwa is renewed too)
                  </div>
                )}
              </div>
              <div className="hidden md:block w-[130px] flex-shrink-0">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{docLabel[tab]}</div>
                <div className="text-xs text-slate-600 mt-0.5 font-mono">{docValue(e)}</div>
              </div>
              <div className="hidden md:block w-[120px] flex-shrink-0">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{dateLabel}</div>
                <div className="text-xs text-slate-600 mt-0.5">
                  {e.expiryDate || '—'}
                  {e.alertType === 'leave' && e.leaveCycle && <span className="block text-[10px] text-slate-400">Cycle {e.leaveCycle} · 42 days (30 paid)</span>}
                </div>
              </div>
              <span className={`w-[150px] flex-shrink-0 text-center text-[11.5px] font-bold px-3 py-1.5 rounded-full border whitespace-nowrap ${lvl[e.level]?.badge}`}>
                {e.alertType === 'leave'
                  ? (e.days < 0 ? `Overdue ${Math.abs(e.days)}d` : `Due in ${e.days}d`)
                  : (e.days < 0 ? `Expired ${Math.abs(e.days)}d ago` : `${e.days}d left`)}
              </span>
            </div>
          ))}

          {/* Pagination */}
          {filtered.length > 0 && (
            <div className="flex items-center justify-between px-6 py-3.5 border-t border-slate-100">
              <span className="text-[12.5px] text-slate-400">
                Showing {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, filtered.length)} of {filtered.length}
              </span>
              <div className="flex gap-1.5">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:border-[#fb866d] hover:text-[#e8614a] disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                  <ChevronLeftIcon size={14} />
                </button>
                {pages.map(p => (
                  <button key={p} onClick={() => setPage(p)}
                    className={`w-8 h-8 rounded-lg border text-[13px] font-semibold transition-all ${page === p ? 'bg-[#fb866d] border-[#fb866d] text-white' : 'border-slate-200 text-slate-600 hover:border-[#fb866d] hover:text-[#e8614a]'
                      }`}>
                    {p}
                  </button>
                ))}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:border-[#fb866d] hover:text-[#e8614a] disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                  <ChevronRightIcon size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}